import { useEffect, useRef, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, Trash2, Image as ImageIcon } from "lucide-react";

const MAX_SIZE = 1_000_000; // 1 MB

export default function LogoPlantel() {
  const { user } = useAuth();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        setPlanteles(data);
        if (data.length) {
          setPlantelId(data[0].id);
          setPreview(data[0].logo_url || "");
        }
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!plantelId) return;
    const p = planteles.find((x) => x.id === plantelId);
    setPreview(p?.logo_url || "");
  }, [plantelId, planteles]);

  const plantel = planteles.find((p) => p.id === plantelId);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Solo imágenes (PNG, JPG, SVG)"); return; }
    if (f.size > MAX_SIZE) { toast.error("Imagen muy grande. Máx 1 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!plantelId) return;
    setSaving(true);
    try {
      await api.put(`/planteles/${plantelId}`, { logo_url: preview || null });
      toast.success("Logo guardado");
      const { data } = await api.get("/planteles");
      setPlanteles(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = () => setPreview("");

  return (
    <AppLayout>
      <div className="max-w-3xl">
        <div className="mb-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Documentos imprimibles</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Logo del plantel</h1>
          <p className="text-sm text-[#545454] mt-1">Este logo se usará en las credenciales y otros documentos oficiales del plantel.</p>
        </div>

        {["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role) && planteles.length > 0 && (
          <div className="mb-4">
            <label className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1 block">Plantel</label>
            <Select value={plantelId} onValueChange={setPlantelId}>
              <SelectTrigger data-testid="logo-plantel-select" className="h-9 w-96"><SelectValue /></SelectTrigger>
              <SelectContent>
                {planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="bg-white border border-[#E5E7EB] rounded p-6">
          <div className="flex gap-6 items-center">
            <div className="w-40 h-40 border-2 border-dashed border-[#D1D5DB] rounded flex items-center justify-center bg-[#F9FAFB] overflow-hidden shrink-0">
              {preview ? (
                <img src={preview} alt="Logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <div className="text-center text-[#9CA3AF]">
                  <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                  <div className="text-xs">Sin logo</div>
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-[#10312B]">{plantel?.nombre || "—"}</div>
              <div className="text-xs text-[#6B7280] mb-3">CCT: {plantel?.cct || "—"}</div>
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="logo-file-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-logo-upload">
                  <Upload className="w-4 h-4 mr-1.5" /> {preview ? "Cambiar logo" : "Subir logo"}
                </Button>
                {preview && (
                  <Button variant="outline" onClick={remove} className="text-[#9F2241] border-[#9F2241]" data-testid="btn-logo-remove">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Quitar
                  </Button>
                )}
              </div>
              <div className="text-[11px] text-[#9CA3AF] mt-2">PNG, JPG o SVG. Máx 1 MB. Recomendado: cuadrado, fondo transparente.</div>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2 border-t border-[#F3F4F6] pt-4">
            <Button data-testid="btn-logo-save" disabled={saving} onClick={save} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
